'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { useEffect } from 'react'

import { meta, pixelsEnabled } from '@/config/analytics'
import { clickIdsForServerEvent } from '@/lib/analytics/attribution'
import { firePixelViewContent } from '@/lib/analytics/pixels'

/**
 * Records the ad pixels' `ViewContent` for the page it is mounted on, twice over: once from the
 * browser, once from this site's own server.
 *
 * Both fires share one uuid. The browser half goes to Meta and TikTok directly (`eventID` /
 * `event_id`); the server half is a same-origin POST to `/api/view-content`, which captures
 * `content_viewed` into PostHog with the visitor's IP, user agent and first-party ad cookies, and
 * from there the Meta destination sends the Conversions API twin. Meta deduplicates the pair on
 * the id. The server half is what survives an ad blocker; the browser half is what carries the
 * pixel's own cookies to Meta. Neither waits for PostHog's browser SDK, which loads lazily and
 * would miss a visitor who leaves within two seconds — `keepalive` lets the POST outlive the page.
 *
 * Fires once per mount — on a cold load and on a client-side navigation alike, because a route
 * change mounts a fresh page tree. Deliberately a page-level component rather than a branch in
 * `PixelPageviews`: that hook skips the first render, which is exactly the ad landing this event
 * is for. Neither platform's fire depends on its script having loaded by then:
 * `firePixelViewContent` holds each until the pixel's global is on the page.
 *
 * The server half waits too, for the pixel's `_fbp` cookie (spec
 * `2026-09-30-view-content-twin-match-keys.md`). The route reads the ad cookies from the request, and
 * on a cold ad landing the effect ran before `fbevents.js` had written `_fbp` — so half the twins
 * reached Meta with IP and user agent only, which Meta does not count as a match key, and Events
 * Manager flagged every `ViewContent` for missing `user_data`. See `afterFbp` for the bounds.
 */
export function PixelViewContent({ contentName }: { contentName: string }) {
  useEffect(() => {
    if (!pixelsEnabled) {
      return
    }

    const eventId = newEventId()
    firePixelViewContent(contentName, eventId)

    // Read now, not when the POST leaves: by then a client-side navigation may have changed both.
    const { fbclid, ttclid, seenAt } = clickIdsForServerEvent()
    const body = JSON.stringify({
      eventId,
      content: contentName,
      url: window.location.href,
      ...(document.referrer ? { referrer: document.referrer } : {}),
      ...(fbclid ? { fbclid } : {}),
      ...(ttclid ? { ttclid } : {}),
      ...(seenAt ? { seenAt } : {}),
    })
    const post = () =>
      void fetch('/api/view-content', {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json' },
        body,
      }).catch(() => {
        // The browser half has already fired; a lost server twin is not worth surfacing to a visitor.
      })

    // Without a Meta pixel there is no `_fbp` to wait for, and TikTok's twin needs nothing more.
    if (meta.pixelId) {
      afterFbp(post)
    } else {
      post()
    }
    // No cleanup on purpose: the view has happened, so a navigation inside the wait must not cancel
    // the twin. The pending poll and the page-hidden listeners end themselves once it has been sent.
  }, [contentName])

  return null
}

/**
 * Runs `send` once `_fbp` is in `document.cookie`, or after {@link FBP_WAIT_ATTEMPTS} ×
 * {@link FBP_WAIT_INTERVAL_MS} (3 s) without it, or when the page is hidden — whichever comes first,
 * and only once.
 *
 * The deadline is what keeps the ad-blocked visitor: with the pixel blocked `_fbp` never appears,
 * and that visitor's twin is the reason the server half exists. A late twin costs nothing, because
 * Meta deduplicates it with the browser fire on the shared event id. Hiding covers the bounce that
 * leaves inside the wait, through two events: `pagehide` for a navigation or a closed tab, and
 * `visibilitychange` to `hidden` for the cases `pagehide` never sees — switching app on iOS, or a
 * background tab the browser later freezes or discards without unloading it. The POST is
 * `keepalive`, so it outlives the page either way.
 */
function afterFbp(send: () => void): void {
  let sent = false
  const once = () => {
    if (sent) {
      return
    }
    sent = true
    window.removeEventListener('pagehide', once)
    document.removeEventListener('visibilitychange', onHidden)
    send()
  }
  const onHidden = () => {
    if (document.visibilityState === 'hidden') {
      once()
    }
  }
  window.addEventListener('pagehide', once)
  document.addEventListener('visibilitychange', onHidden)

  const poll = (attempt: number) => {
    if (sent) {
      return
    }
    if (hasCookie('_fbp') || attempt >= FBP_WAIT_ATTEMPTS) {
      once()
      return
    }
    window.setTimeout(() => poll(attempt + 1), FBP_WAIT_INTERVAL_MS)
  }
  poll(0)
}

const FBP_WAIT_INTERVAL_MS = 250
const FBP_WAIT_ATTEMPTS = 12

function hasCookie(name: string): boolean {
  return document.cookie.split(';').some((pair) => pair.trimStart().startsWith(`${name}=`))
}

/**
 * A v4 uuid. `crypto.randomUUID` needs a secure context, which `pnpm dev` on plain http is not, so
 * the fallback builds one from `getRandomValues`.
 */
function newEventId(): string {
  // Typed as optional on purpose: the lib types declare it unconditionally, the runtime does not.
  const { randomUUID } = crypto as Crypto & { randomUUID?: () => string }
  if (randomUUID) {
    return randomUUID.call(crypto)
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
